import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "@core/contexts/AuthContext";
import { useSoloTrial } from "@core/hooks/useSoloTrial";
import { UsageAnalyticsTracker } from "@core/services/UsageAnalyticsTracker";
import { PromoBanner } from "./PromoBanner";

export type PromoPlacement = "vote_page" | "results_page";

interface PromoSlotProps {
  placement: PromoPlacement;
  /**
   * Page de résultats uniquement : le compte à rebours d'essai ne s'adresse
   * qu'au créateur du sondage. Un participant n'a pas à connaître l'état
   * d'abonnement de quelqu'un d'autre.
   */
  isCreator?: boolean;
}

/**
 * L'emplacement promotionnel des pages vote et résultats.
 *
 * POURQUOI UN « SLOT » ET NON UNE CARTE DE PLUS. Ces deux pages portaient
 * déjà `PromoBanner` (« crée ton propre sondage »), affiché à tout le monde
 * sans condition. Ajouter la promotion de l'essai à côté aurait donné deux
 * appels à l'action empilés, dont l'un souvent hors sujet. Un seul emplacement
 * donc, et une règle qui choisit le message : l'essai quand il a quelque chose
 * à dire, la bannière générique sinon. `PromoBanner` reste inchangée et reste
 * le défaut.
 *
 * QUATRE PUBLICS, PAS UN. Un message unique serait faux pour au moins l'un
 * d'eux — c'est le point qui décide si cet emplacement est utile ou gênant :
 *
 *   - visiteur anonyme : c'est le seul public à qui l'essai sert d'argument
 *     d'acquisition. Il est venu voter pour quelqu'un d'autre, sans compte et
 *     sans en chercher un ;
 *   - compte Gratuit n'ayant jamais essayé : la cible directe de l'offre ;
 *   - essai en cours : JAMAIS une offre, seulement le compte à rebours, et
 *     seulement au créateur sur la page de résultats. Proposer un essai à
 *     quelqu'un qui l'a déjà est l'incohérence qui décrédibilise le reste ;
 *   - payant, ou essai déjà consommé : rien de spécifique — le tableau de bord
 *     porte déjà ce message, le répéter ici serait du harcèlement.
 *
 * Tout l'état vient de `useSoloTrial()`. Aucune dérivation nouvelle : c'est la
 * leçon des groupes, où trois calculs indépendants du même état se
 * contredisaient.
 */
export const PromoSlot: React.FC<PromoSlotProps> = ({
  placement,
  isCreator = false,
}) => {
  const { t } = useTranslation("polls");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isActive, daysRemaining, canStart } = useSoloTrial();

  const go = useCallback(
    (to: string) => {
      void UsageAnalyticsTracker.track("upgrade_cta_clicked", {
        source: "promo_slot",
        placement,
      });
      navigate(to);
    },
    [navigate, placement],
  );

  if (!user) {
    return (
      <Encart
        texte={t("trial_promo.anon")}
        ctaLabel={t("trial_promo.anon_cta")}
        // `/essai` et non une route d'inscription : cette page possède déjà
        // le cas « pas connecté » et son propre appel à l'action. Deviner ici
        // une seconde destination créerait un deuxième chemin à maintenir —
        // et `/auth`, vers lequel `Voting.tsx` navigue encore, n'existe même
        // pas (la route attrape-tout `/:handle` l'avale).
        onCta={() => go("/essai")}
        testId="promo-slot-trial-anon"
      />
    );
  }

  if (isActive && daysRemaining !== null) {
    // Le compte à rebours n'a de sens que pour la personne concernée, et
    // seulement là où elle regarde son propre sondage.
    if (placement === "results_page" && isCreator) {
      return (
        <Encart
          texte={t("trial_promo.active_countdown", { count: daysRemaining })}
          ctaLabel={t("trial_promo.active_cta")}
          onCta={() => go("/pricing")}
          testId="promo-slot-trial-active"
        />
      );
    }
    return <PromoBanner />;
  }

  if (canStart) {
    return (
      <Encart
        texte={t("trial_promo.free")}
        ctaLabel={t("trial_promo.free_cta")}
        onCta={() => go("/essai")}
        testId="promo-slot-trial-free"
      />
    );
  }

  return <PromoBanner />;
};

const Encart: React.FC<{
  texte: string;
  ctaLabel: string;
  onCta: () => void;
  testId: string;
}> = ({ texte, ctaLabel, onCta, testId }) => (
  <div
    data-testid={testId}
    className="border-t border-white/5 bg-background dark:bg-[#0a0a0a] py-3 px-4"
  >
    <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
      <span className="flex min-w-0 items-center gap-2 text-xs text-gray-500">
        <Sparkles className="w-3.5 h-3.5 flex-shrink-0 text-primary" />
        <span>{texte}</span>
      </span>
      <button
        type="button"
        onClick={onCta}
        className="flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-medium text-blue-400 transition-colors hover:text-blue-300"
      >
        {ctaLabel}
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  </div>
);
